import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router";

import { useMe } from "../lib/queries";
import { useTitle } from "../lib/useTitle";
import { ErrorState, NotFound } from "./ErrorState";
import { Loading } from "./Loading";

interface Props {
  children: ReactNode;
  /** Accounts waiting for approval may open this page (their account settings). */
  allowPending?: boolean;
}

export function RequireAuth({ children, allowPending }: Props) {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <div className="page"><Loading /></div>;
  if (me.isError) return <ErrorState error={me.error} onRetry={() => me.refetch()} />;
  if (!me.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/connexion?suite=${next}`} replace />;
  }
  if (!me.data.approved && !allowPending) return <PendingApproval email={me.data.email} />;
  return children;
}

/** Non-admins get a plain 404: the admin area does not advertise itself. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useMe();
  return (
    <RequireAuth>
      {me.data?.is_admin ? children : <NotFound />}
    </RequireAuth>
  );
}

function PendingApproval({ email }: { email: string }) {
  useTitle("Compte en attente de validation");
  return (
    <div className="page page--narrow">
      <div className="centered-state" role="status">
        <h1>Votre compte attend sa validation</h1>
        <p>
          Un administrateur doit valider <strong>{email}</strong> avant l'ouverture de votre espace. Vous recevrez un
          e-mail dès que ce sera fait ; les alertes démarreront à ce moment-là.
        </p>
        <div className="actions">
          <Link className="button button--quiet" to="/compte">
            Mon compte
          </Link>
        </div>
      </div>
    </div>
  );
}
